/**
 * 热敏打印机 WebUSB 直连（ESC/POS）
 *
 * 浏览器 → navigator.usb → 内置字库渲染 → 清晰
 *
 * 注意：
 * 1) 必须用 Chrome / Edge（支持 WebUSB）
 * 2) Windows 默认的 USB 打印支持驱动会独占接口 → 需用 Zadig 把驱动换成 WinUSB
 * 3) 首次调用 requestDevice() 触发浏览器授权弹窗，授权信息写入 localStorage 后下次自动重连
 */

// ESC/POS 打印机的 USB 类码：0x07 = Printer Class
const PRINTER_CLASS = 0x07;

// 缓存设备键
const DEVICE_CACHE_KEY = 'thermal_printer_usb_device';

// 常见 ESC/POS 厂商 ID（得力 / Epson / 启锐 / 公章等）
const KNOWN_VENDORS = [
  { vendorId: 0x04b8 }, // Epson
  { vendorId: 0x0416 }, // Winbond / 常见国产热敏打印机
  { vendorId: 0x0fe6 }, // ICS Advent
  { vendorId: 0x1a86 }, // 启锐 / 国产兼容
  { vendorId: 0x0525 }, // PLD / 兼容
];

/**
 * 浏览器是否支持 WebUSB
 */
export function isWebUSPSupported() {
  return typeof navigator !== 'undefined' && !!navigator.usb;
}

/**
 * 列出已授权过的设备（无需再弹窗）
 */
export async function listAuthorizedDevices() {
  if (!isWebUSPSupported()) return [];
  try {
    return await navigator.usb.getDevices();
  } catch (e) {
    console.warn('[WebUSB] getDevices 失败:', e);
    return [];
  }
}

/**
 * 从 navigator.usb 设备对象里提取稳定的身份信息（用于 localStorage 缓存）
 */
function getDeviceFingerprint(dev) {
  return {
    vendorId: dev.vendorId,
    productId: dev.productId,
    serialNumber: dev.serialNumber || '',
    productName: dev.productName || ''
  };
}

/**
 * 把字节流写入 USB 端点
 */
async function writeToDevice(dev, bytes) {
  if (!dev.opened) await dev.open();
  if (dev.configuration === null) await dev.selectConfiguration(1);

  // 找到打印机接口（interfaceClass === 0x07）
  const iface = dev.configuration.interfaces.find(i => {
    const alt = i.alternates[0];
    return alt && alt.interfaceClass === PRINTER_CLASS;
  });
  if (!iface) {
    throw new Error('未找到打印机接口（class 0x07），请检查 Zadig 驱动是否为 WinUSB');
  }
  const interfaceNumber = iface.interfaceNumber;

  if (dev.claimedInterface === null || dev.claimedInterface !== interfaceNumber) {
    try { await dev.claimInterface(interfaceNumber); } catch (e) {
      // 已 claim 过就忽略
      if (!String(e.message || '').includes('already')) throw e;
    }
  }

  // 找 OUT 端点
  const alt = iface.alternates[0];
  const ep = alt.endpoints.find(e => e.direction === 'out');
  if (!ep) throw new Error('未找到打印机 OUT 端点');

  // WebUSB transferOut 一次最大 16KB（high-speed）/ 64KB（super-speed），分片发送
  const CHUNK = 16384;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, Math.min(i + CHUNK, bytes.length));
    await dev.transferOut(ep.endpointNumber, slice);
  }
}

/**
 * 请求用户授权并连接打印机（首次会弹授权框）
 * @returns {Promise<USBDevice>}
 */
export async function requestAndConnectPrinter() {
  if (!isWebUSPSupported()) {
    throw new Error('当前浏览器不支持 WebUSB，请使用 Chrome 或 Edge 浏览器');
  }

  // 先看本地缓存有没有之前授权过的设备
  const cached = readCachedFingerprint();
  let dev = null;
  if (cached) {
    const authorized = await listAuthorizedDevices();
    dev = authorized.find(d =>
      d.vendorId === cached.vendorId &&
      d.productId === cached.productId &&
      (cached.serialNumber === '' || d.serialNumber === cached.serialNumber)
    ) || null;
  }

  // 没缓存或缓存失效 → 弹授权框
  if (!dev) {
    dev = await navigator.usb.requestDevice({
      filters: [
        { classCode: PRINTER_CLASS },
        ...KNOWN_VENDORS
      ]
    });
    saveCachedFingerprint(getDeviceFingerprint(dev));
  }

  await dev.open();
  return dev;
}

/**
 * 把 ESC/POS 字节流发送给热敏打印机
 * @param {Uint8Array} bytes - escpos.js buildReceiptBytes 的产物
 * @returns {Promise<USBDevice>}
 */
export async function printToThermalPrinter(bytes) {
  const dev = await requestAndConnectPrinter();
  try {
    await writeToDevice(dev, bytes);
  } finally {
    // 不主动 close，让设备保持授权状态供下次快速重连
  }
  return dev;
}

/**
 * 打印并自动切纸（短别名，escpos.js 已经包含切纸指令，调用同 printToThermalPrinter）
 */
export const printReceipt = printToThermalPrinter;

// ===== 设备指纹持久化 =====

function readCachedFingerprint() {
  try {
    const raw = localStorage.getItem(DEVICE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveCachedFingerprint(fp) {
  try {
    localStorage.setItem(DEVICE_CACHE_KEY, JSON.stringify(fp));
  } catch (e) {
    console.warn('[WebUSB] 写入设备缓存失败:', e);
  }
}

/**
 * 清除已缓存的打印机授权（用户切换打印机时用）
 */
export function clearPrinterCache() {
  localStorage.removeItem(DEVICE_CACHE_KEY);
}